import type Graph from 'graphology';
import React, { useMemo } from 'react';

import { hasDocument } from '../../../constants/GraphRagConstants';
import { SelectableStatsPanel } from './SelectableStatsPanel';

type ClustersPanelProps = {
  graph: Graph;
  clusterFilter: Set<number>;
  onToggle: (cluster: number) => void;
  onSetAll: (clusters: number[]) => void;
  documentId?: string;
};

export const ClustersPanel = ({
  graph,
  clusterFilter,
  onToggle,
  onSetAll,
  documentId,
}: ClustersPanelProps) => {
  const { totalNodes, items } = useMemo(() => {
    const clusters = new Map<number, { count: number; color: string; label: string }>();
    let visibleNodeCount = 0;

    graph.forEachNode((_nodeId, attrs) => {
      const docs = attrs.documents as unknown;

      if (!hasDocument(docs, documentId)) return;

      const cluster = (attrs.cluster as number) ?? 0;
      const clusterColor = (attrs.clusterColor as string) || '#999';
      const clusterLabel = (attrs.clusterLabel as string) || `Cluster ${cluster}`;

      const existing = clusters.get(cluster);

      if (existing) {
        if (!attrs.hidden) {
          existing.count += 1;
        }
      } else {
        clusters.set(cluster, {
          count: attrs.hidden ? 0 : 1,
          color: clusterColor,
          label: clusterLabel,
        });
      }

      if (!attrs.hidden) {
        visibleNodeCount += 1;
      }
    });

    return {
      totalNodes: visibleNodeCount,
      items: Array.from(clusters.entries())
        .sort((a, b) => b[1].count - a[1].count)
        .map(([id, info]) => ({
          id,
          label: info.label,
          count: info.count,
          color: info.color,
          tooltipLabel: 'cluster',
        })),
    };
  }, [graph, documentId]);

  return (
    <SelectableStatsPanel
      items={items}
      totalNodes={totalNodes}
      selectedIds={clusterFilter}
      onToggle={onToggle}
      onClear={() => onSetAll([])}
      footerText={`${items.length} communities detected (Louvain)`}
      maxHeight={200}
    />
  );
};
